/**
 * Sandbox Manager
 * Provides secure command execution with isolation
 * Inspired by Claude Code's sandbox mode
 */

import { spawn, execSync, SpawnOptions } from 'child_process';
import fs from 'fs';
import path from 'path';
import os from 'os';
import {
  SandboxConfig,
  SandboxResult,
  SandboxCapabilities,
  DEFAULT_SANDBOX_CONFIG,
  SAFE_COMMANDS,
  DANGEROUS_COMMANDS,
} from './types';

export class SandboxManager {
  private config: SandboxConfig;
  private projectRoot: string;
  private capabilities: SandboxCapabilities | null = null;

  constructor(projectRoot: string, config?: Partial<SandboxConfig>) {
    this.projectRoot = projectRoot;
    this.config = { ...DEFAULT_SANDBOX_CONFIG, ...config };

    // Add project root to allowed paths
    if (!this.config.allowedPaths.includes(projectRoot)) {
      this.config.allowedPaths.push(projectRoot);
    }

    // Expand home directory in paths
    this.config.deniedPaths = this.config.deniedPaths.map(p =>
      p.replace(/^~/, os.homedir())
    );
  }

  /**
   * Check available sandbox methods on this system
   */
  getCapabilities(): SandboxCapabilities {
    if (this.capabilities) {
      return this.capabilities;
    }

    const methods: string[] = [];
    let recommended: string | undefined;

    const platform = os.platform();

    if (platform === 'linux') {
      // Check for bubblewrap
      if (this.commandExists('bwrap')) {
        methods.push('bubblewrap');
        recommended = 'bubblewrap';
      }

      // Check for firejail
      if (this.commandExists('firejail')) {
        methods.push('firejail');
        if (!recommended) recommended = 'firejail';
      }
    } else if (platform === 'darwin') {
      // Check for sandbox-exec (built into macOS)
      if (this.commandExists('sandbox-exec')) {
        methods.push('sandbox-exec');
        recommended = 'sandbox-exec';
      }
    }

    // Docker is available on all platforms
    if (this.commandExists('docker')) {
      methods.push('docker');
      if (!recommended) recommended = 'docker';
    }

    this.capabilities = {
      available: methods.length > 0,
      methods,
      recommended,
      reason: methods.length === 0 ? 'No sandbox tools found (install bubblewrap, firejail, or docker)' : undefined,
    };

    return this.capabilities;
  }

  /**
   * Check if a command exists
   */
  private commandExists(command: string): boolean {
    try {
      execSync(`which ${command}`, { stdio: 'pipe' });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if sandbox is enabled and available
   */
  isEnabled(): boolean {
    return this.config.enabled && this.getCapabilities().available;
  }

  /**
   * Enable/disable sandbox
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if a command is safe to run without sandbox
   */
  isSafeCommand(command: string): boolean {
    const normalizedCmd = command.trim().toLowerCase();

    // Check against safe commands
    for (const safe of SAFE_COMMANDS) {
      if (normalizedCmd.startsWith(safe.toLowerCase())) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a command is dangerous and must be sandboxed
   */
  isDangerousCommand(command: string): boolean {
    const normalizedCmd = command.trim().toLowerCase();

    for (const dangerous of DANGEROUS_COMMANDS) {
      if (normalizedCmd.includes(dangerous.toLowerCase())) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a command should be sandboxed
   */
  shouldSandbox(command: string): boolean {
    if (!this.config.enabled) return false;
    if (!this.getCapabilities().available) return false;

    // Always sandbox dangerous commands
    if (this.isDangerousCommand(command)) return true;

    // Don't sandbox safe commands
    if (this.isSafeCommand(command)) return false;

    // Sandbox everything else when enabled
    return true;
  }

  /**
   * Execute a command with optional sandboxing
   */
  async execute(
    command: string,
    options: {
      cwd?: string;
      env?: Record<string, string>;
      timeout?: number;
      forceSandbox?: boolean;
      forceNoSandbox?: boolean;
    } = {}
  ): Promise<SandboxResult> {
    const startTime = Date.now();
    const cwd = options.cwd || this.projectRoot;
    const timeout = options.timeout || this.config.timeout;

    // Determine if we should sandbox
    const shouldSandbox = options.forceNoSandbox
      ? false
      : options.forceSandbox || this.shouldSandbox(command);

    if (shouldSandbox && this.getCapabilities().available) {
      return this.executeInSandbox(command, cwd, options.env, timeout);
    } else {
      return this.executeUnsandboxed(command, cwd, options.env, timeout);
    }
  }

  /**
   * Execute command without sandbox
   */
  private executeUnsandboxed(
    command: string,
    cwd: string,
    env?: Record<string, string>,
    timeout?: number
  ): Promise<SandboxResult> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let stdout = '';
      let stderr = '';
      let killed = false;

      const child = spawn('sh', ['-c', command], {
        cwd,
        env: { ...process.env, ...env },
        stdio: ['pipe', 'pipe', 'pipe'],
      });

      const timeoutId = timeout
        ? setTimeout(() => {
            killed = true;
            child.kill('SIGTERM');
          }, timeout)
        : null;

      child.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      child.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      child.on('close', (code, signal) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: code ?? 1,
          stdout: stdout.trim(),
          stderr: stderr.trim(),
          killed,
          signal: signal || undefined,
          duration_ms: Date.now() - startTime,
          sandboxed: false,
        });
      });

      child.on('error', (error) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: 1,
          stdout: '',
          stderr: '',
          killed: false,
          duration_ms: Date.now() - startTime,
          sandboxed: false,
          error: error.message,
        });
      });
    });
  }

  /**
   * Execute command in sandbox
   */
  private executeInSandbox(
    command: string,
    cwd: string,
    env?: Record<string, string>,
    timeout?: number
  ): Promise<SandboxResult> {
    const method = this.config.method || this.getCapabilities().recommended || 'none';

    switch (method) {
      case 'bubblewrap':
        return this.executeWithBubblewrap(command, cwd, env, timeout);
      case 'firejail':
        return this.executeWithFirejail(command, cwd, env, timeout);
      case 'sandbox-exec':
        return this.executeWithSandboxExec(command, cwd, env, timeout);
      case 'docker':
        return this.executeWithDocker(command, cwd, env, timeout);
      default:
        // Fallback to unsandboxed
        return this.executeUnsandboxed(command, cwd, env, timeout);
    }
  }

  /**
   * Execute with bubblewrap (Linux)
   */
  private executeWithBubblewrap(
    command: string,
    cwd: string,
    env?: Record<string, string>,
    timeout?: number
  ): Promise<SandboxResult> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let stdout = '';
      let stderr = '';
      let killed = false;

      // Build bubblewrap arguments
      const bwrapArgs = [
        '--unshare-all',
        '--die-with-parent',
        '--ro-bind', '/usr', '/usr',
        '--ro-bind', '/lib', '/lib',
        '--ro-bind', '/bin', '/bin',
        '--symlink', '/usr/lib64', '/lib64',
        '--proc', '/proc',
        '--dev', '/dev',
        '--tmpfs', '/tmp',
      ];

      // Add allowed paths
      for (const allowedPath of this.config.allowedPaths) {
        if (fs.existsSync(allowedPath)) {
          bwrapArgs.push('--bind', allowedPath, allowedPath);
        }
      }

      // Add read-only paths
      for (const roPath of this.config.readOnlyPaths) {
        if (fs.existsSync(roPath)) {
          bwrapArgs.push('--ro-bind', roPath, roPath);
        }
      }

      // Network isolation
      if (!this.config.allowNetwork) {
        bwrapArgs.push('--unshare-net');
      }

      // Set working directory
      bwrapArgs.push('--chdir', cwd);

      // Add the command
      bwrapArgs.push('sh', '-c', command);

      const child = spawn('bwrap', bwrapArgs, {
        env: this.filterEnv(env),
        stdio: ['pipe', 'pipe', 'pipe'],
      });

      const timeoutId = timeout
        ? setTimeout(() => {
            killed = true;
            child.kill('SIGTERM');
          }, timeout)
        : null;

      child.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      child.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      child.on('close', (code, signal) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: code ?? 1,
          stdout: stdout.trim(),
          stderr: stderr.trim(),
          killed,
          signal: signal || undefined,
          duration_ms: Date.now() - startTime,
          sandboxed: true,
          method: 'bubblewrap',
        });
      });

      child.on('error', (error) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: 1,
          stdout: '',
          stderr: '',
          killed: false,
          duration_ms: Date.now() - startTime,
          sandboxed: false,
          error: error.message,
        });
      });
    });
  }

  /**
   * Execute with firejail (Linux)
   */
  private executeWithFirejail(
    command: string,
    cwd: string,
    env?: Record<string, string>,
    timeout?: number
  ): Promise<SandboxResult> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let stdout = '';
      let stderr = '';
      let killed = false;

      // Build firejail arguments
      const fjArgs = [
        '--noprofile',
        '--quiet',
        '--private-tmp',
      ];

      // Network isolation
      if (!this.config.allowNetwork) {
        fjArgs.push('--net=none');
      }

      // Add allowed paths as whitelist
      for (const allowedPath of this.config.allowedPaths) {
        fjArgs.push(`--whitelist=${allowedPath}`);
      }

      // Memory limit
      if (this.config.maxMemoryMB) {
        fjArgs.push(`--rlimit-as=${this.config.maxMemoryMB * 1024 * 1024}`);
      }

      // Add the command
      fjArgs.push('--', 'sh', '-c', `cd ${cwd} && ${command}`);

      const child = spawn('firejail', fjArgs, {
        env: this.filterEnv(env),
        stdio: ['pipe', 'pipe', 'pipe'],
      });

      const timeoutId = timeout
        ? setTimeout(() => {
            killed = true;
            child.kill('SIGTERM');
          }, timeout)
        : null;

      child.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      child.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      child.on('close', (code, signal) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: code ?? 1,
          stdout: stdout.trim(),
          stderr: stderr.trim(),
          killed,
          signal: signal || undefined,
          duration_ms: Date.now() - startTime,
          sandboxed: true,
          method: 'firejail',
        });
      });

      child.on('error', (error) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: 1,
          stdout: '',
          stderr: '',
          killed: false,
          duration_ms: Date.now() - startTime,
          sandboxed: false,
          error: error.message,
        });
      });
    });
  }

  /**
   * Execute with sandbox-exec (macOS)
   */
  private executeWithSandboxExec(
    command: string,
    cwd: string,
    env?: Record<string, string>,
    timeout?: number
  ): Promise<SandboxResult> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let stdout = '';
      let stderr = '';
      let killed = false;

      // Create sandbox profile
      const profile = this.createSandboxExecProfile(cwd);

      const child = spawn('sandbox-exec', ['-p', profile, 'sh', '-c', `cd ${cwd} && ${command}`], {
        env: this.filterEnv(env),
        stdio: ['pipe', 'pipe', 'pipe'],
      });

      const timeoutId = timeout
        ? setTimeout(() => {
            killed = true;
            child.kill('SIGTERM');
          }, timeout)
        : null;

      child.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      child.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      child.on('close', (code, signal) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: code ?? 1,
          stdout: stdout.trim(),
          stderr: stderr.trim(),
          killed,
          signal: signal || undefined,
          duration_ms: Date.now() - startTime,
          sandboxed: true,
          method: 'sandbox-exec',
        });
      });

      child.on('error', (error) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: 1,
          stdout: '',
          stderr: '',
          killed: false,
          duration_ms: Date.now() - startTime,
          sandboxed: false,
          error: error.message,
        });
      });
    });
  }

  /**
   * Create sandbox-exec profile for macOS
   */
  private createSandboxExecProfile(cwd: string): string {
    const allowedPaths = [...this.config.allowedPaths, cwd]
      .map(p => `(subpath "${p}")`)
      .join('\n            ');

    const readOnlyPaths = this.config.readOnlyPaths
      .map(p => `(subpath "${p}")`)
      .join('\n            ');

    const networkRule = this.config.allowNetwork
      ? '(allow network*)'
      : '(deny network*)';

    return `
(version 1)
(deny default)

; Allow read access to system paths
(allow file-read*
    ${readOnlyPaths}
)

; Allow read/write to project paths
(allow file*
    ${allowedPaths}
)

; Allow process execution
(allow process-exec*)
(allow process-fork)

; Allow signals
(allow signal)

; Allow sysctl reads
(allow sysctl-read)

; Network
${networkRule}

; Allow mach services needed for execution
(allow mach-lookup)
    `.trim();
  }

  /**
   * Execute with Docker
   */
  private executeWithDocker(
    command: string,
    cwd: string,
    env?: Record<string, string>,
    timeout?: number
  ): Promise<SandboxResult> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let stdout = '';
      let stderr = '';
      let killed = false;

      // Build docker arguments
      const dockerArgs = [
        'run',
        '--rm',
        '--security-opt=no-new-privileges',
        '-w', '/workspace',
        '-v', `${cwd}:/workspace`,
      ];

      // Network isolation
      if (!this.config.allowNetwork) {
        dockerArgs.push('--network=none');
      }

      // Memory limit
      if (this.config.maxMemoryMB) {
        dockerArgs.push(`--memory=${this.config.maxMemoryMB}m`);
      }

      // Add environment variables
      const filteredEnv = this.filterEnv(env);
      for (const [key, value] of Object.entries(filteredEnv)) {
        dockerArgs.push('-e', `${key}=${value}`);
      }

      // Use alpine as a minimal image
      dockerArgs.push('alpine:latest', 'sh', '-c', command);

      const child = spawn('docker', dockerArgs, {
        stdio: ['pipe', 'pipe', 'pipe'],
      });

      const timeoutId = timeout
        ? setTimeout(() => {
            killed = true;
            child.kill('SIGTERM');
          }, timeout)
        : null;

      child.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      child.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      child.on('close', (code, signal) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: code ?? 1,
          stdout: stdout.trim(),
          stderr: stderr.trim(),
          killed,
          signal: signal || undefined,
          duration_ms: Date.now() - startTime,
          sandboxed: true,
          method: 'docker',
        });
      });

      child.on('error', (error) => {
        if (timeoutId) clearTimeout(timeoutId);

        resolve({
          exitCode: 1,
          stdout: '',
          stderr: '',
          killed: false,
          duration_ms: Date.now() - startTime,
          sandboxed: false,
          error: error.message,
        });
      });
    });
  }

  /**
   * Filter environment variables based on config
   */
  private filterEnv(env?: Record<string, string>): Record<string, string> {
    if (!this.config.allowEnv) {
      return {};
    }

    const baseEnv = { ...process.env, ...env };
    const filtered: Record<string, string> = {};

    if (this.config.allowedEnvVars) {
      for (const key of this.config.allowedEnvVars) {
        if (baseEnv[key]) {
          filtered[key] = baseEnv[key]!;
        }
      }
    } else {
      return baseEnv as Record<string, string>;
    }

    return filtered;
  }

  /**
   * Get current config
   */
  getConfig(): SandboxConfig {
    return { ...this.config };
  }

  /**
   * Update config
   */
  updateConfig(config: Partial<SandboxConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Global sandbox manager instance
let globalSandboxManager: SandboxManager | null = null;

export function getSandboxManager(projectRoot?: string): SandboxManager {
  if (!globalSandboxManager) {
    globalSandboxManager = new SandboxManager(projectRoot || process.cwd());
  }
  return globalSandboxManager;
}

export function resetSandboxManager(): void {
  globalSandboxManager = null;
}
